<template>
    <div
        data-testid="toast-message"
        class="toast-message p-sm gap-sm flex w-fit rounded-md"
        :class="[severityClasses.bg, severityClasses.border]"
    >
        <div class="flex justify-center">
            <i
                class="h-6 w-6"
                :class="{
                    'i-borderless-check': props.type === 'check',
                    'i-warning': props.type === 'warning',
                    'i-red-x': props.type === 'error',
                    'i-info-circle': props.type === 'info',
                }"
            ></i>
        </div>
        <!-- The colour class sits on the <p>, not the wrapper: the base layer
             styles `p { @apply text-dark-500 }`, which beats inheritance. -->
        <p class="flex items-center" :class="severityClasses.text">{{ message }}</p>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, type PropType } from 'vue';
import { useOrganizationStore } from '@/stores/organization';
import { useAdminStore } from '@/stores/admin';
import { useNotificationStore } from '@/stores/notification';
import type { ToastType } from '@/stores/store-interfaces';

const organizationStore = useOrganizationStore();
const adminStore = useAdminStore();
const notificationStore = useNotificationStore();
const props = defineProps({
    message: {
        type: String,
        required: true,
    },
    type: {
        type: String as PropType<ToastType>,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    },
    store: {
        type: String as PropType<'org' | 'admin' | 'notification'>,
        required: false,
        default: 'org',
    },
});

// Every severity resolves to the same charcoal surface in light mode — the
// --toast-* tokens only diverge under `.dark`. The border colour is bound here
// but the border itself is drawn only in dark mode (see the style block), so
// light-mode toasts keep their exact pre-dark-mode geometry.
const severityClasses = computed(() => {
    switch (props.type) {
        case 'check':
            return { bg: 'bg-toast-success', text: 'text-toast-success', border: 'border-toast-success' };
        case 'warning':
            return { bg: 'bg-toast-warning', text: 'text-toast-warning', border: 'border-toast-warning' };
        case 'error':
            return { bg: 'bg-toast-danger', text: 'text-toast-danger', border: 'border-toast-danger' };
        case 'info':
        default:
            return { bg: 'bg-toast-info', text: 'text-toast-info', border: 'border-toast-info' };
    }
});

onMounted(() => {
    setTimeout(() => {
        if (props.store === 'org') {
            organizationStore.clearToastMessage();
        } else if (props.store === 'admin') {
            adminStore.clearToastMessage();
        } else {
            notificationStore.clearToastMessage();
        }
    }, 5000);
});
</script>

<style scoped>
@reference '@/assets/styles/default.css';

toast-message {
    transition:
        transform 0.5s ease,
        opacity 0.5s ease;
}

/* Dark mode only: the tinted severity surfaces need a border to separate them
   from the dark canvas. Light mode keeps the borderless charcoal toast, so the
   border-width is applied here rather than as a utility class in the template
   — that keeps the light-mode box geometry byte-identical to pre-dark-mode. */
.dark .toast-message {
    border: 1px solid;
}

.toast-enter-active,
.toast-leave-active {
    transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
    opacity: 0;
    transform: translateX(20px);
}
</style>
